/**
 * @module app.track.filters
 */
import appStateservice from '../stateservice.js';
import {TRACK_FILTER_ID} from "../constants";

/**
 * @return {angular.Directive} The directive specs.
 * this is a generic function, maybe move it elsewhere?
 */
export function ngEnter() {
  return {
    require: ['ngModel'],
    link: function(scope, elm, attrs, ctrls) {
      elm.bind("keydown keypress", function(event) {
        if (event.which === 13) {
          scope.$apply(function() {
            if (attrs['ngEnter']) {
              scope.$eval(attrs['ngEnter']);
            }
          });
          elm.blur();
          event.preventDefault();
        }
      });
    }
  };
}

class Controller {
  /**
   * @param {app.track.service} appTrackService
   * @param {app.track.filterService} appTrackFilterService
   * @param {angularGettext.Catalog} gettextCatalog
   * @param {ui.router.$state} $state
   * @param {app.stateservice} appStateservice
   * @param {jQuery} $element
   * @param {angular.$timeout} $timeout Timeout service.
   * @ngInject
   */
  constructor(appTrackService, appTrackFilterService, gettextCatalog, $state, appStateservice, $element, $timeout) {

    /**
     * @type {app.track.service}
     * @export
     */
    this.appTrackService = appTrackService;

    /**
     * @type {app.track.filterService}
     * @export
     */
    this.appTrackFilterService = appTrackFilterService;

    /**
     * @type {angularGettext.Catalog}
     * @protected
     */
    this.gettextCatalog = gettextCatalog;

    /**
     * @type {ui.router.$state}
     * @private
     */
    this.routingState_ = $state;

    /**
     * @type {app.stateservice}
     * @private
     */
    this.stateservice_ = appStateservice;

    /**
     * @private
     */
    this.element_ = $element;

    /**
     * @type {angular.$timeout}
     * @private
     */
    this.timeout_ = $timeout;

    /**
     * @type {boolean}
     * @export
     */
    this.newFilterEnabled = false;

    /**
     * @type {string}
     * @export
     */
    this.newFilterName = '';

    /**
     * 2 because a) -1 is already used for "other" and b) we use negative ids for new filters, so 2 will give -2 practically
     * @type {number}
     * @private
     */
    this.newFilterId_ = 2;

  }


  $onInit() {
    this.appTrackFilterService.init();
  }

  /* public methods */

  /**
   * @export
   * @return {boolean}
   */
  isEditable() {
    return this.appTrackService.ownSelectedTrack() && !document.body.classList.contains('in-maintenance');
  }

  /**
   * @export
   * @return {string}
   */
  routingStateName() {
    return this.routingState_.current.name;
  }

  /**
   * @param {number} filterId
   * @export
   */
  updateSelectedFilter(filterId) {
    if (!this.appTrackFilterService.selectedFilter || this.appTrackFilterService.selectedFilter.id !== filterId) {
      this.appTrackFilterService.selectedFilter = this.appTrackFilterService.filtersListPending.find(f => f.id === filterId) || this.appTrackFilterService.getDefaultFilter();
      if (this.routingStateName() == 'track-facts') {
        // assign selected filter to selected track
        this.appTrackFilterService.setFilterToTrack();
        // directly save filter change if we are in track-facts
        this.appTrackFilterService.saveDirect();
      }
      // when not in track-facts, handle the setFilterToTrack from track save
    }
  }

  /**
   * Add new filter localy
   * @export
   */
  addFilter() {
    const newFilter = this.createNewFilterObjet_(this.newFilterName);
    if (!newFilter) {
      return;
    }
    this.appTrackFilterService.addFilterAndSort(newFilter);
    // clear the new filter input
    this.newFilterName = '';
    // hide new filter input
    this.newFilterEnabled = false;
  }

  /**
   * Delete filter on save action
   * @param {number} filterId
   * @export
   */
  deleteFilter(filterId) {
    this.appTrackFilterService.deleteFilter(filterId);
  }

  /**
   * @export
   */
  saveFilters() {
    this.appTrackFilterService.save();
    this.routingState_.go(this.stateservice_.getTransitionFrom('track-facts'));
  }

  /**
   * @export
   */
  cancelFilters() {
    this.appTrackFilterService.cleanUp();
    this.routingState_.go(this.stateservice_.getTransitionFrom('track-facts'));
  }

  /**
   * @export
   * @return {string}
   */
  getTrackFilterText() {
    const selectedTrack = this.appTrackService.selectedTrack;
    if (!selectedTrack) {
      return this.gettextCatalog.getString('Filters');
    }
    if (!this.appTrackService.ownSelectedTrack()) {
      const filterName = selectedTrack.properties['filter_name'];
      return filterName ? filterName : this.gettextCatalog.getString(this.appTrackFilterService.defaultName);
    }
    const selectedFilter = this.appTrackFilterService.selectedFilter;
    if (!selectedFilter || (selectedTrack.id === undefined && selectedFilter.id === -1)) {
      // track has not finished loading or is a new track, use default filter
      if (!selectedFilter) {
        this.appTrackFilterService.selectFilterFromTrack();
      }
      return this.gettextCatalog.getString('Filters');
    }
    let filterName = selectedFilter.name;
    if (selectedFilter.id === TRACK_FILTER_ID.OTHER) {
      filterName = this.gettextCatalog.getString(filterName);
    }
    return filterName;
  }

  /**
   * @export
   * @param {jQuery.Event|ol.events.Event} event
   */
  addNewEmptyFilter(event) {
    // prevent dropdown to close when clicking on the +
    event.stopPropagation();
    // add new filter (if set) if user click again on +
    if (this.newFilterEnabled && this.newFilterName != '') {
      this.addFilter();
    }
    this.newFilterEnabled = true;
    // focus on input
    const el = this.element_.find('.new-filter-input')[0];
    this.timeout_(function() {
      // need to use a timeout because the ng-show isnt immediate
      el.focus();
    });
  }

  /* utility methods */

  /**
   * @private
   * @param {string} name
   * @return {?appx.TrackFilter}
   */
  createNewFilterObjet_(name) {
    if (!name) {
      return null; // we do not have any filter name yet...
    }
    const filter = this.appTrackFilterService.filtersListPending.find(f => f.name === name);
    if (filter) {
      bootbox.alert({
        message: this.gettextCatalog.getString('duplicated filter name error')
      });
      return null;
    }
    return {name, id: this.getNewFilterId_()};
  }

  /**
   * @private
   * @return {number}
   */
  getNewFilterId_() {
    const newId = this.newFilterId_;
    this.newFilterId_ += 1;
    // using negative id for new filters
    return newId * -1;
  }

}

const module = angular.module('appTrackFilters', [appStateservice.name]);

module.directive('ngEnter', ngEnter);

/**
 * @const {!angular.Component}
 */
const component = {
  controller: Controller,
  templateUrl: 'js/app/track/filters/partial.html',
};

module.component('appTrackFilters', component);

const light = {};

/**
 * @const {!angular.Component}
 */
light.component = {
  controller: Controller,
  templateUrl: 'js/app/track/filters/partial_light.html',
};

module.component('appTrackFiltersLight', light.component);


export default module;
